import {Spinner} from 'react-bootstrap'
import Pagination from './pagination'
import {GridSortModel} from './pagination.types'
import './table.scss'

export interface DataTableProps<T> {
  items: T[]
  isLoading: boolean
  columns: Column<T>[]
  sortData?: GridSortModel
  handleSortedChange?: (sortData: GridSortModel) => void
  pageSize?: number
  totalData?: number
  handleChangePage: (page: number) => void
  handleChangSizeData: (pageSize: number) => void
  page: number
  onRowClick?: (item: T) => void
  onRowHover?: (item: T, isHovering: boolean) => void
  paginationType?: 'index' | 'cursor'
  hasNext?: boolean
  hasPrev?: boolean
  onNext?: () => void
  onPrev?: () => void
}

export interface Column<T> {
  id: string
  label: React.ReactNode
  sortable?: boolean
  className?: string
  width?: number
  Cell?: (item: T, index: number) => React.ReactNode
}
const DataTable = <
  T extends {
    id?: string
  }
>({
  items,
  isLoading,
  columns,
  sortData,
  handleSortedChange,
  handleChangSizeData,
  handleChangePage,
  pageSize,
  totalData,
  page,
  onRowClick,
  onRowHover,
  paginationType = 'index',
  hasNext,
  hasPrev,
  onNext,
  onPrev,
}: DataTableProps<T>) => {
  // Calculate start and end item indices for the current page
  const itemsPerPage = pageSize || 10
  const startItem = page * itemsPerPage + 1
  let endItem = startItem + items.length - 1
  if (endItem > (totalData || 0)) {
    endItem = totalData || 0
  }

  return (
    <div className='tab-content'>
      <div className='table-responsive  max-h-750'>
        <table className='table table-bordered sticky-header table-last-column table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
          <thead>
            <tr className='fw-bold text-muted text-uppercase'>
              {columns.map((column) => {
                return (
                  <th
                    key={column.id as string}
                    onClick={() =>
                      handleSortedChange &&
                      column.sortable &&
                      handleSortedChange({
                        field: column.id as string,
                        sort:
                          sortData?.field === column.id
                            ? sortData.sort === 'desc'
                              ? 'asc'
                              : 'desc'
                            : 'desc',
                      })
                    }
                    className={column.className}
                    style={{
                      width: column.width ? `${column.width}px` : 'auto',
                      cursor: column.sortable ? 'pointer' : 'default',
                    }}
                  >
                    {column.sortable && (
                      <>
                        {sortData?.field === column.id ? (
                          sortData.sort === 'desc' ? (
                            <i className='bi bi-arrow-up-short me-2'></i>
                          ) : (
                            <i className='bi bi-arrow-down-short me-2'></i>
                          )
                        ) : (
                          <i className='bi bi-arrow-down-up me-2'></i>
                        )}
                      </>
                    )}

                    {column.label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className='text-gray-600'>
            {isLoading ? (
              <tr>
                <td
                  colSpan={7 + columns.length + (columns.length > 3 ? columns.length - 3 : 0)}
                  className='text-center'
                >
                  <Spinner />
                </td>
              </tr>
            ) : (
              <>
                {items.length > 0 ? (
                  items.map((val) => (
                    <tr
                      key={val.id}
                      onClick={() => onRowClick?.(val)}
                      onMouseEnter={() => onRowHover?.(val, true)}
                      onMouseLeave={() => onRowHover?.(val, false)}
                      style={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        transition: 'background-color 0.2s ease',
                      }}
                      className={onRowClick ? 'hover-row' : ''}
                    >
                      {columns.map((column, index) => {
                        const cellValue = val[column.id as keyof T] as React.ReactNode
                        return (
                          <td key={column.id as string} className={column.className}>
                            {column.Cell ? column.Cell(val, index) : cellValue}
                          </td>
                        )
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={9 + columns.length + (columns.length > 3 ? columns.length - 3 : 0)}
                      className='text-center'
                    >
                      <b>Data not found</b>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {Boolean(totalData) && (
        <Pagination
          limit={pageSize ?? 0}
          onLimitChange={handleChangSizeData}
          onPageChange={handleChangePage}
          isLoading={isLoading}
          total={totalData ?? 0}
          totalPages={Math.ceil((totalData ?? 0) / (pageSize ?? 10))}
          loadedData={{
            startLoad: startItem,
            endLoad: endItem,
          }}
          paginationType={paginationType}
          hasNext={hasNext}
          hasPrev={hasPrev}
          onNext={onNext}
          onPrev={onPrev}
        />
      )}
    </div>
  )
}

export default DataTable
